import { PLUGIN_ID_TO_PROTO_CLASS } from "@superblocksteam/shared";

import { uiLogger } from "core/telemetry/logger";

/**
 * Convert a raw datasource configuration (wire-format JSON from the API) into
 * a plain, Redux-serializable object via `protoClass.fromJson(...)` followed
 * by `structuredClone`. Preserves the {case, value} oneof discriminator shape
 * downstream form code relies on (e.g. `connection.auth.method.case` in the
 * salesforce template); does NOT invoke `Message.toJSON()`, which would emit
 * wire-format JSON and drop the discriminator.
 *
 * Without `structuredClone` the saga stores a protobuf-es Message class
 * instance, which fails RTK's serializability check (constructor !== Object).
 *
 * Use `toPlainProtoMessage` directly for callers that already have a
 * deserialized value (e.g. produced by a custom projection deserializer).
 */
export function normalizeDatasourceConfiguration(
  pluginId: string,
  rawConfiguration: Record<string, unknown>,
): Record<string, unknown> {
  const protoClass = PLUGIN_ID_TO_PROTO_CLASS[pluginId];
  if (!protoClass) {
    return rawConfiguration;
  }
  return toPlainProtoMessage(
    protoClass.fromJson(rawConfiguration, { ignoreUnknownFields: true }),
    pluginId,
  );
}

/**
 * Deep-clone a protobuf-es Message (or any value) into a Redux-serializable
 * plain object. Logs `pluginId` + payload key list before re-throwing if
 * `structuredClone` fails (DataCloneError on non-cloneable values).
 */
export function toPlainProtoMessage<T>(value: T, pluginId: string): T {
  try {
    return structuredClone(value);
  } catch (e) {
    uiLogger.error("toPlainProtoMessage: structuredClone failed", {
      pluginId,
      keys: Object.keys(value as Record<string, unknown>),
    });
    throw e;
  }
}
